import { useState } from 'react'
import { PageLoader } from '../../shared/ui/Spinner'
import { AlertsPanel } from './admin/AlertsPanel'
import { DashboardHeader } from './admin/DashboardHeader'
import { FunnelChart } from './admin/FunnelChart'
import { KpiCards } from './admin/KpiCards'
import { ManagersTable } from './admin/ManagersTable'
import { MatricesPieChart } from './admin/MatricesPieChart'
import { ProcurementTable } from './admin/ProcurementTable'
import { StatusBar } from './admin/StatusBar'
import { TopClientsTable } from './admin/TopClientsTable'
import { TopPartsTable } from './admin/TopPartsTable'
import { AllManagersModal } from './admin/modals/AllManagersModal'
import { ClientDetailModal } from './admin/modals/ClientDetailModal'
import { ExportReportModal } from './admin/modals/ExportReportModal'
import { ManagerDetailModal } from './admin/modals/ManagerDetailModal'
import { PartPriceHistoryModal } from './admin/modals/PartPriceHistoryModal'
import { ProcurementDetailModal } from './admin/modals/ProcurementDetailModal'
import { UserActivityModal } from './admin/modals/UserActivityModal'
import { useDashboardData } from './admin/useDashboardData'
import { useDashboardPeriod } from './admin/useDashboardPeriod'
import { NewsFeedWidget } from '../news/NewsFeedWidget'
import { PlanFactRating } from './admin/PlanFactRating'
import { ManagerChips } from './admin/ManagerChips'
import { ClientsAnalyticsSection } from './admin/ClientsAnalyticsSection'
import { FollowUpAlertsPanel } from './FollowUpAlertsPanel'

type ModalState =
  | { type: 'manager'; id: string }
  | { type: 'all-managers' }
  | { type: 'procurement'; id: string }
  | { type: 'part'; partNumber: string; brand: string | null }
  | { type: 'client'; id: string }
  | { type: 'users' }
  | { type: 'export' }
  | null

export function AdminDashboard() {
  const { state: period, setKey, setCustom } = useDashboardPeriod()
  const { data, isLoading, isError, dataUpdatedAt, refetch } = useDashboardData(period)
  const [modal, setModal] = useState<ModalState>(null)

  if (isLoading) return <PageLoader />
  if (isError || !data) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
        <p className="text-sm text-red-700 mb-2">Не удалось загрузить дашборд</p>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
        >
          Повторить
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <DashboardHeader
        period={period.key}
        onPeriodChange={setKey}
        customFrom={period.from ? period.from.slice(0, 10) : null}
        customTo={period.to ? period.to.slice(0, 10) : null}
        onCustomChange={(from, to) =>
          setCustom(new Date(from).toISOString(), new Date(`${to}T23:59:59`).toISOString())
        }
        fetchedAt={dataUpdatedAt}
        onExport={() => setModal({ type: 'export' })}
      />

      {/* Единый селектор менеджеров — управляет рейтингами, donut'ом и heatmap.
          Selection хранится в users.gridSettings.planningSelection. */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <ManagerChips />
      </div>

      {/* «Контроль КП»: просроченные/сегодняшние контакты с клиентами.
          Скоуп считает бэк: ADMIN/ГД — все менеджеры, РОП — менеджеры его городов. */}
      <FollowUpAlertsPanel showManager />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlanFactRating
          periodKey={period.key}
          customFrom={period.from}
          customTo={period.to}
          kind="financial"
        />
        <PlanFactRating
          periodKey={period.key}
          customFrom={period.from}
          customTo={period.to}
          kind="akb"
        />
      </div>

      <ClientsAnalyticsSection
        periodKey={period.key}
        customFrom={period.from}
        customTo={period.to}
      />

      <KpiCards
        kpi={data.kpi}
        deadlineTo={data.period.to.slice(0, 10)}
        onUserActivityClick={() => setModal({ type: 'users' })}
      />

      {/* График «Выручка компании» скрыт по решению встречи 17.09.2026:
          не кликался, не делился по менеджерам, «Подтверждено»/«Оплачено» —
          заглушки с нулями. Данные в ответе API остались (revenue), вернуть
          можно одним компонентом. */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <FunnelChart funnel={data.funnel} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ManagersTable
          managers={data.managers}
          onRowClick={(id) => setModal({ type: 'manager', id })}
          onShowAll={() => setModal({ type: 'all-managers' })}
        />
        <ProcurementTable
          procurement={data.procurement}
          onRowClick={(id) => setModal({ type: 'procurement', id })}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <TopPartsTable
            topParts={data.topParts}
            onRowClick={(t) => setModal({ type: 'part', partNumber: t.partNumber, brand: t.brand })}
          />
        </div>
        <div className="lg:col-span-2">
          <MatricesPieChart matrices={data.matrices} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopClientsTable
          clients={data.topClients}
          onRowClick={(id) => setModal({ type: 'client', id })}
        />
        <AlertsPanel alerts={data.alerts} />
      </div>

      <StatusBar rates={data.rates} />

      <NewsFeedWidget />

      {modal?.type === 'manager' && (
        <ManagerDetailModal managerId={modal.id} period={period} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'all-managers' && (
        <AllManagersModal
          onClose={() => setModal(null)}
          onPickManager={(id) => setModal({ type: 'manager', id })}
        />
      )}
      {modal?.type === 'procurement' && (
        <ProcurementDetailModal userId={modal.id} period={period} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'part' && (
        <PartPriceHistoryModal
          target={{ partNumber: modal.partNumber, brand: modal.brand }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'client' && (
        <ClientDetailModal clientId={modal.id} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'users' && (
        <UserActivityModal users={data.userActivity} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'export' && (
        <ExportReportModal
          defaultPeriodKey={
            period.key === 'month' || period.key === 'quarter' || period.key === 'year'
              ? period.key
              : period.key === 'custom'
                ? 'custom'
                : 'month'
          }
          defaultFrom={period.from}
          defaultTo={period.to}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}
